import { zodResolver } from "@hookform/resolvers/zod";
import axios from "axios";
import { AlertCircleIcon, Loader2Icon } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { z } from "zod";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import Logo from "../../components/Logo";
import config from "../../config";
import { useNotificationsProvider } from "../../context/NotificationsContext";
import { useAcceptInvitationMutation } from "../../services/api";

function inferErrorKey(error: unknown): string {
  const e = error as { data?: { error?: string; message?: string } };
  const code = e?.data?.error;
  if (code === "EXPIRED_TOKEN") return "accept-invitation.error-expired";
  if (code === "TOKEN_ALREADY_USED") return "accept-invitation.error-already-used";
  if (code === "INVALID_TOKEN") return "accept-invitation.error-invalid";
  if (e?.data?.message) return e.data.message;
  return "accept-invitation.error-default";
}

interface Invitee {
  email: string;
  firstName?: string;
}

export default function AcceptInvitation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [acceptInvitation] = useAcceptInvitationMutation();
  const { showNotification } = useNotificationsProvider();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");
  const emailFromUrl = searchParams.get("email");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validating, setValidating] = useState(true);
  const [invitee, setInvitee] = useState<Invitee | null>(null);

  // Pre-validate the token via GET /auth/validate-invitation. With the
  // Cognito flow the token is a JWT signed with INVITATION_SECRET — the
  // backend verifies signature/expiry/single-use and returns the invitee's
  // email + firstName so we can confirm the right person is at this URL.
  // The legacy flow's bcrypt-token endpoint returns 400 here, in which case
  // we fall back to the form using the URL params (acceptInvitation will
  // then dispatch to whichever backend usecase is configured).
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setError(t("accept-invitation.missing-params"));
      setValidating(false);
      return undefined;
    }
    axios
      .get(`${config.apiBaseUrl}/auth/validate-invitation`, {
        params: { token },
      })
      .then(({ data }) => {
        if (cancelled) return;
        if (data && data.valid) {
          setInvitee({ email: data.email, firstName: data.firstName });
        } else {
          setError(t("accept-invitation.error-invalid"));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.data?.error;
        if (code === "EXPIRED_TOKEN") {
          setError(t("accept-invitation.error-expired"));
        } else if (code === "TOKEN_ALREADY_USED") {
          setError(t("accept-invitation.error-already-used"));
        } else if (err?.response?.status === 400) {
          // Legacy bcrypt token: validate-invitation rejects it. Fall through
          // to the form so the existing legacy submit path can still work.
          setInvitee({ email: emailFromUrl || "" });
        } else {
          setError(t("accept-invitation.error-default"));
        }
      })
      .finally(() => {
        if (!cancelled) setValidating(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, emailFromUrl, t]);

  // Password rules align with the Cognito User Pool policy
  // (infra/cognito/serverless.yml): MinimumLength=10, RequireUppercase,
  // RequireLowercase, RequireNumbers, RequireSymbols=false. Keep the
  // client-side checks in sync; if the pool changes, update both.
  const schema = useMemo(
    () =>
      z
        .object({
          email: z
            .string()
            .min(1, t("accept-invitation.required-email"))
            .email(t("accept-invitation.invalid-email")),
          password: z
            .string()
            .min(10, t("accept-invitation.password-too-short"))
            .regex(/[A-Z]/, t("accept-invitation.password-missing-uppercase"))
            .regex(/[a-z]/, t("accept-invitation.password-missing-lowercase"))
            .regex(/[0-9]/, t("accept-invitation.password-missing-number")),
          confirmPassword: z.string(),
        })
        .refine((data) => data.password === data.confirmPassword, {
          path: ["confirmPassword"],
          message: t("accept-invitation.passwords-must-match"),
        }),
    [t]
  );
  type Values = z.infer<typeof schema>;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: invitee?.email || emailFromUrl || "",
      password: "",
      confirmPassword: "",
    },
  });

  // Once validation resolves and we know the invitee email, re-seed the
  // email field so the form reflects the verified address (the form mounts
  // before validation finishes).
  useEffect(() => {
    const resolved = invitee?.email || emailFromUrl;
    if (resolved) form.setValue("email", resolved);
  }, [invitee, emailFromUrl, form]);

  async function onSubmit(values: Values) {
    if (!token) {
      setError(t("accept-invitation.missing-token"));
      return;
    }
    setError(null);
    try {
      await acceptInvitation({
        token,
        email: values.email,
        password: values.password,
      }).unwrap();
      showNotification("success", t("accept-invitation.success"));
      navigate("/setup-2fa?from=invitation");
    } catch (err) {
      setError(t(inferErrorKey(err)));
    }
  }

  const submitDisabled =
    !token || !invitee || form.formState.isSubmitting;
  const emailDisabled = !!(invitee?.email || emailFromUrl);

  if (validating) {
    return (
      <PageShell title={t("accept-invitation.title")}>
        <div className="flex flex-col items-center gap-4 py-6">
          <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            {t("accept-invitation.validating", "Validating invitation…")}
          </p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell title={t("accept-invitation.title")}>
      <Form {...form}>
        <form
          autoComplete="off"
          noValidate
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <div className="space-y-2">
            <h1 className="text-2xl font-bold tracking-tight">
              {invitee?.firstName
                ? t("accept-invitation.title-greeting", "Welcome, {{firstName}}", {
                    firstName: invitee.firstName,
                  })
                : t("accept-invitation.title")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t("accept-invitation.subtitle")}
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircleIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("accept-invitation.email-label")}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="username"
                    disabled={emailDisabled}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("accept-invitation.password-label")}
                </FormLabel>
                <FormControl>
                  <PasswordInput
                    autoComplete="new-password"
                    show={showPassword}
                    onToggleShow={() => setShowPassword((s) => !s)}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t("accept-invitation.password-requirements")}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("accept-invitation.confirm-password-label")}
                </FormLabel>
                <FormControl>
                  <PasswordInput
                    autoComplete="new-password"
                    show={showConfirmPassword}
                    onToggleShow={() => setShowConfirmPassword((s) => !s)}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-col items-center gap-3">
            <Button
              type="submit"
              size="lg"
              className="w-full"
              disabled={submitDisabled}
            >
              {t("accept-invitation.submit-button")}
            </Button>
            <Button asChild variant="link" size="sm">
              <RouterLink to="/forgot-password">
                {t("accept-invitation.link-expired")}
              </RouterLink>
            </Button>
            <Button asChild variant="link" size="sm">
              <RouterLink to="/login">
                {t("accept-invitation.back-to-login")}
              </RouterLink>
            </Button>
          </div>
        </form>
      </Form>
    </PageShell>
  );
}

// ---------- Layout shell shared by the validating + form states ----

interface PageShellProps {
  title: string;
  children: React.ReactNode;
}

function PageShell({ title, children }: PageShellProps) {
  return (
    <>
      <Helmet>
        <title>{title} | Betterfield</title>
      </Helmet>
      <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center px-6 py-12">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>
        {children}
      </main>
    </>
  );
}
